import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchCommunityGradingTree } from '../../../services/gradingService';
import type { GradingTreePayload } from '../../Workspace/admin/grading/types';
import { getCalibrationForFamily, normalizeGradingTreePayload } from '../../Workspace/admin/grading/utils';
import { matchRoleByScore, questionsByFactor, visibleAnswersForQuestion } from '../../Workspace/admin/recordGrading/gradingEngine';
import type { GradingAnswerSnapshot, GradingResultSnapshot } from './marketProfileStore';
import { MpStepper } from './MarketProfileShared';

type Step = 'family' | 'specialization' | 'cluster' | 'scoring';

const STEP_LABELS = ['Направление', 'Роль', 'Детали функционала'];

function stepIndex(step: Step): number {
  if (step === 'family' || step === 'specialization') return 0;
  if (step === 'cluster') return 1;
  return 2;
}

type Props = {
  onComplete: (result: GradingResultSnapshot, answers: GradingAnswerSnapshot[]) => void;
  onBack: () => void;
};

export default function GradingQuestionnaire({ onComplete, onBack }: Props) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tree, setTree] = useState<GradingTreePayload | null>(null);

  const [step, setStep] = useState<Step>('family');
  const [familyId, setFamilyId] = useState<number | null>(null);
  const [specId, setSpecId] = useState<number | null>(null);
  const [clusterId, setClusterId] = useState<number | null>(null);
  const [calibAnswerId, setCalibAnswerId] = useState<number | null>(null);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<Record<number, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitStartedRef = useRef(false);

  const loadTree = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = normalizeGradingTreePayload(await fetchCommunityGradingTree());
      if (!data.families.length) {
        throw new Error('В базе пока нет направлений для грейдинга');
      }
      setTree(data);
    } catch (err) {
      setTree(null);
      setLoadError(err instanceof Error ? err.message : 'Не удалось загрузить анкету грейдинга');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const families = tree?.families ?? [];
  const specializations = useMemo(() => tree?.specializations ?? [], [tree]);
  const roleClusters = useMemo(() => tree?.roleClusters ?? [], [tree]);

  const familySpecs = useMemo(
    () => (familyId ? specializations.filter((s) => s.family_id === familyId) : []),
    [specializations, familyId],
  );

  const familyClusters = useMemo(
    () => (familyId ? roleClusters.filter((c) => c.family_id === familyId) : []),
    [roleClusters, familyId],
  );

  const calib = useMemo(
    () => (tree && familyId
      ? getCalibrationForFamily(familyId, tree.calibrationQuestions, tree.calibrationAnswers)
      : { question: null, answers: [] }),
    [tree, familyId],
  );

  const activeClusterId = clusterId
    ?? calib.answers.find((a) => a.id === calibAnswerId)?.target_role_cluster_id
    ?? null;

  const factorQuestions = useMemo(() => {
    if (!tree || !familyId || !activeClusterId) return [];
    return questionsByFactor(activeClusterId, familyId, tree.factors, tree.questions);
  }, [tree, familyId, activeClusterId]);

  const answeredCount = useMemo(
    () => factorQuestions.filter(({ question }) => selectedAnswerIds[question.id] != null).length,
    [factorQuestions, selectedAnswerIds],
  );

  const totalScore = useMemo(
    () => Object.values(selectedAnswerIds).reduce((sum, answerId) => {
      const answer = tree?.answers.find((a) => a.id === answerId);
      return sum + (answer?.answer_score ?? 0);
    }, 0),
    [selectedAnswerIds, tree],
  );

  const matchedRole = useMemo(() => {
    if (!tree || !activeClusterId) return undefined;
    return matchRoleByScore(tree.roles, activeClusterId, specId, totalScore);
  }, [tree, activeClusterId, specId, totalScore]);

  if (loading) {
    return (
      <div className="dcf-mp-form">
        <p className="text-sm text-slate-500 py-10 text-center">Загрузка анкеты грейдинга…</p>
      </div>
    );
  }

  if (loadError || !tree) {
    return (
      <div className="dcf-mp-form">
        <button type="button" className="dcf-btn dcf-btn--ghost dcf-mp-back" onClick={onBack}>
          ← К профилю рынка
        </button>
        <div className="dcf-mp-card dcf-mp-form-card mt-4 text-center">
          <p className="dcf-mp-error">{loadError ?? 'Не удалось загрузить анкету'}</p>
          <p className="dcf-mp-form-hint mt-2">
            Анкета берётся из базы данных проекта. Проверьте подключение и попробуйте снова.
          </p>
          <button type="button" className="dcf-btn dcf-btn--primary mt-4" onClick={() => void loadTree()}>
            Повторить загрузку
          </button>
        </div>
      </div>
    );
  }

  const familyName = families.find((f) => f.id === familyId)?.name ?? '';
  const clusterName = roleClusters.find((c) => c.id === activeClusterId)?.name ?? '';
  const specName = specId != null
    ? specializations.find((s) => s.id === specId)?.name ?? null
    : null;

  const finish = () => {
    if (submitStartedRef.current || !matchedRole) return;
    submitStartedRef.current = true;
    setSubmitting(true);
    setError(null);

    const result: GradingResultSnapshot = {
      familyName,
      specializationName: specName,
      roleClusterName: clusterName,
      totalScore,
      roleName: matchedRole.name ?? null,
      gradeLevel: matchedRole.grade_level ?? null,
    };
    const answerSnapshots: GradingAnswerSnapshot[] = factorQuestions.flatMap(({ factor, question }) => {
      const answerId = selectedAnswerIds[question.id];
      if (answerId == null) return [];
      const answer = visibleAnswersForQuestion(tree.answers, question.id).find(
        (a) => a.id === answerId,
      );
      return [{
        questionId: question.id,
        factorName: factor.name,
        questionText: question.question_text,
        answerText: answer?.answer_text ?? '',
        answerScore: answer?.answer_score ?? 0,
      }];
    });
    onComplete(result, answerSnapshots);
  };

  const tryCompleteFromScoring = () => {
    if (factorQuestions.length === 0 || answeredCount < factorQuestions.length) return;
    if (!matchedRole) {
      setError('Не удалось сопоставить уровень роли. Измените ответы и попробуйте снова.');
      return;
    }
    finish();
  };

  const showExitBack = step === 'family' || step === 'specialization' || step === 'cluster';

  return (
    <div className="dcf-mp-form">
      {showExitBack && !submitting && (
        <button type="button" className="dcf-btn dcf-btn--ghost dcf-mp-back" onClick={onBack}>
          ← К профилю рынка
        </button>
      )}

      {submitting && (
        <p className="dcf-mp-form-hint" style={{ marginBottom: '0.75rem' }}>
          Сохраняем результат грейдинга…
        </p>
      )}

      <header className="dcf-header">
        <h1 className="dcf-header__title">Грейдинг роли</h1>
        <p className="dcf-header__desc">
          Грейдинг помогает определить рыночный эквивалент вашей роли по фактической зоне
          ответственности, а не только по названию должности.
        </p>
      </header>

      <MpStepper steps={STEP_LABELS} current={stepIndex(step)} />

      <div className="dcf-mp-card dcf-mp-form-card">
        {step === 'family' && (
          <>
            <h2 className="dcf-mp-form-section-title">Выберите направление</h2>
            <p className="dcf-mp-form-hint">К какой функциональной области ближе всего ваша роль.</p>
            <div className="dcf-mp-option-grid">
              {families.map((family) => (
                <button
                  key={family.id}
                  type="button"
                  className={`dcf-mp-option-card ${familyId === family.id ? 'dcf-mp-option-card--active' : ''}`}
                  onClick={() => {
                    setFamilyId(family.id);
                    setSpecId(null);
                    setClusterId(null);
                    setCalibAnswerId(null);
                    setSelectedAnswerIds({});
                    setError(null);
                  }}
                >
                  {family.name}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'specialization' && (
          <>
            <h2 className="dcf-mp-form-section-title">Специализация</h2>
            <p className="dcf-mp-form-hint">Если применимо к вашей роли.</p>
            <div className="dcf-mp-option-list">
              {familySpecs.map((spec) => (
                <label key={spec.id} className={`dcf-mp-answer ${specId === spec.id ? 'dcf-mp-answer--selected' : ''}`}>
                  <input
                    type="radio"
                    name="mp-spec"
                    checked={specId === spec.id}
                    onChange={() => setSpecId(spec.id)}
                  />
                  <span>{spec.name}</span>
                </label>
              ))}
              <label className={`dcf-mp-answer ${specId === null ? 'dcf-mp-answer--selected' : ''}`}>
                <input
                  type="radio"
                  name="mp-spec"
                  checked={specId === null}
                  onChange={() => setSpecId(null)}
                />
                <span>Без специализации</span>
              </label>
            </div>
          </>
        )}

        {step === 'cluster' && (
          <>
            <h2 className="dcf-mp-form-section-title">Роль</h2>
            {calib.question ? (
              <>
                <p className="dcf-mp-form-hint">{calib.question.question_text}</p>
                <div className="dcf-mp-option-list">
                  {calib.answers.map((a) => (
                    <label key={a.id} className={`dcf-mp-answer ${calibAnswerId === a.id ? 'dcf-mp-answer--selected' : ''}`}>
                      <input
                        type="radio"
                        name="mp-calib"
                        checked={calibAnswerId === a.id}
                        onChange={() => {
                          setCalibAnswerId(a.id);
                          setClusterId(null);
                          setSelectedAnswerIds({});
                          setError(null);
                        }}
                      />
                      <span>{a.answer_text}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="dcf-mp-form-hint">Выберите, что ближе всего описывает вашу зону работы.</p>
                <div className="dcf-mp-option-list">
                  {familyClusters.map((rc) => (
                    <label key={rc.id} className={`dcf-mp-answer ${clusterId === rc.id ? 'dcf-mp-answer--selected' : ''}`}>
                      <input
                        type="radio"
                        name="mp-cluster"
                        checked={clusterId === rc.id}
                        onChange={() => {
                          setClusterId(rc.id);
                          setCalibAnswerId(null);
                          setSelectedAnswerIds({});
                          setError(null);
                        }}
                      />
                      <span>{rc.name}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {step === 'scoring' && (
          <>
            <div className="dcf-mp-scoring-head">
              <h2 className="dcf-mp-form-section-title">Детали функционала</h2>
              <span className="dcf-mp-scoring-count">
                Отвечено {answeredCount} из {factorQuestions.length}
              </span>
            </div>
            <p className="dcf-mp-form-hint">
              Ответьте на каждый вопрос — по ответам определится уровень роли.
            </p>
            {factorQuestions.length === 0 ? (
              <p className="dcf-mp-error">Для выбранной зоны работы пока нет вопросов.</p>
            ) : (
              <>
                {factorQuestions.map(({ factor, question, questionIndexInFactor, questionsInFactor }, i) => {
                  const qAnswers = visibleAnswersForQuestion(tree.answers, question.id);
                  return (
                    <div key={question.id} className="dcf-mp-question">
                      <p className="dcf-mp-question__factor">
                        Вопрос {i + 1} из {factorQuestions.length} · {factor.name}
                        {questionsInFactor > 1 ? ` · ${questionIndexInFactor}/${questionsInFactor}` : ''}
                        {question.name ? ` · ${question.name}` : ''}
                      </p>
                      <p className="dcf-mp-question__text">{question.question_text}</p>
                      {questionIndexInFactor === 1 && factor.description && (
                        <p className="dcf-mp-question__hint">{factor.description}</p>
                      )}
                      <div className="dcf-mp-option-list">
                        {qAnswers.map((a) => (
                          <label
                            key={a.id}
                            className={`dcf-mp-answer ${selectedAnswerIds[question.id] === a.id ? 'dcf-mp-answer--selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name={`mp-q-${question.id}`}
                              checked={selectedAnswerIds[question.id] === a.id}
                              onChange={() => setSelectedAnswerIds((prev) => ({ ...prev, [question.id]: a.id }))}
                            />
                            <span>{a.answer_text}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {error && <p className="dcf-mp-error">{error}</p>}

        <div className="dcf-mp-form-actions">
          {step !== 'scoring' && !submitting && (
            <button
              type="button"
              className="dcf-btn dcf-btn--secondary"
              onClick={() => {
                setError(null);
                if (step === 'family') onBack();
                else if (step === 'specialization') setStep('family');
                else if (step === 'cluster') setStep(familySpecs.length ? 'specialization' : 'family');
              }}
            >
              {step === 'family' ? 'Отмена' : 'Назад'}
            </button>
          )}
          <div className="dcf-mp-form-actions__right">
            {step === 'family' && (
              <button
                type="button"
                className="dcf-btn dcf-btn--primary"
                disabled={!familyId}
                onClick={() => setStep(familySpecs.length ? 'specialization' : 'cluster')}
              >
                Далее
              </button>
            )}
            {step === 'specialization' && (
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={() => setStep('cluster')}>
                Далее
              </button>
            )}
            {step === 'cluster' && (
              <button
                type="button"
                className="dcf-btn dcf-btn--primary"
                onClick={() => {
                  if (!activeClusterId) {
                    setError('Выберите вариант, чтобы продолжить');
                    return;
                  }
                  setError(null);
                  setStep('scoring');
                }}
              >
                К вопросам
              </button>
            )}
            {step === 'scoring' && (
              <button
                type="button"
                className="dcf-btn dcf-btn--primary"
                disabled={
                  submitting
                  || factorQuestions.length === 0
                  || answeredCount < factorQuestions.length
                }
                onClick={tryCompleteFromScoring}
              >
                {submitting ? 'Сохранение…' : 'Завершить грейдинг'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
